import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { extractPersonas, computeSegmentedScore, getScoreColor, getScoreBg } from "./pmfUtils";

export default function PmfSegmentation({ responses, originalScore }) {
    const { t } = useTranslation();
    const [selected, setSelected] = useState(null);
    const personas = useMemo(() => extractPersonas(responses), [responses]);
    const segmented = useMemo(() => computeSegmentedScore(responses, selected), [responses, selected]);

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-1">{t("survey.admin.seg.title")}</h3>
                <p className="text-sm text-slate-500 mb-4">{t("survey.admin.seg.desc")}</p>
                <div className="flex flex-wrap gap-2">
                    {personas.map((p) => (
                        <button key={p.word} onClick={() => setSelected(selected === p.word ? null : p.word)}
                            className={`px-3 py-1.5 text-sm rounded-full border transition-all ${
                                selected === p.word
                                    ? "bg-violet-100 border-violet-400 text-violet-700 font-medium"
                                    : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300"
                            }`}>
                            {p.word} <span className="text-xs opacity-60">({p.count})</span>
                        </button>
                    ))}
                    {personas.length === 0 && <p className="text-sm text-slate-400">{t("survey.admin.noResponses")}</p>}
                </div>
            </div>
            {segmented ? (
                <div className={`rounded-xl border p-6 ${getScoreBg(segmented.score)}`}>
                    <p className="text-sm font-medium text-slate-600 mb-1">
                        {t("survey.admin.seg.segScore")} &mdash; &ldquo;{selected}&rdquo;
                    </p>
                    <div className="flex items-baseline gap-3">
                        <span className={`text-4xl font-bold ${getScoreColor(segmented.score)}`}>{segmented.score}%</span>
                        <span className="text-sm text-slate-500">
                            {segmented.score > originalScore ? "+" : ""}{Math.round((segmented.score - originalScore) * 10) / 10}%
                            {" "}{t("survey.admin.seg.vsOverall")}
                        </span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                        {t("survey.admin.seg.matching", { count: segmented.total })}
                    </p>
                </div>
            ) : (
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 text-center">
                    <p className="text-sm text-slate-400">{t("survey.admin.seg.noSelection")}</p>
                </div>
            )}
            <div className="bg-violet-50 rounded-xl border border-violet-200 p-5">
                <h4 className="text-sm font-semibold text-violet-900 mb-1">{t("survey.admin.seg.hxcTitle")}</h4>
                <p className="text-sm text-violet-700">{t("survey.admin.seg.hxcDesc")}</p>
            </div>
        </div>
    );
}
